import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { acuerdosDeDevolucion, DIAS_PARA_ENFRIARSE } from '../utils/acuerdos';
import { formatearFecha } from '../utils/fecha';

// Los acuerdos de devolución.
//
// Cuando alguien lleva años pagando interés, el prestamista cuadra con él:
// «devuélvame el capital y no le cobro más». Se registra poniéndole 0% al
// préstamo — y desde ese momento el programa deja de mencionarlo. No sale en
// Por cobrar (no hay interés que cobrar) ni en Morosos (no debe nada).
//
// Esta pantalla no los llama morosos ni les inventa una fecha de vencimiento:
// se pactó así. Contesta la única pregunta que nadie contestaba: ¿quién no me
// está devolviendo?

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

const PINTA = {
  sin_empezar:    { color: '#991b1b', fondo: '#fef2f2', borde: '#fecaca', icono: '🔴' },
  enfriado:       { color: '#92400e', fondo: '#fffbeb', borde: '#fde68a', icono: '🟡' },
  devolviendo:    { color: '#166534', fondo: '#f0fdf4', borde: '#bbf7d0', icono: '🟢' },
  recien_pactado: { color: '#3730a3', fondo: '#eef2ff', borde: '#c7d2fe', icono: '🆕' },
  completado:     { color: '#374151', fondo: '#f9fafb', borde: '#e5e7eb', icono: '✅' },
};

const PIE = {
  sin_empezar:    'No han entregado un solo peso desde que se pactó.',
  enfriado:       `Empezaron a devolver y llevan más de ${DIAS_PARA_ENFRIARSE} días sin aparecer.`,
  devolviendo:    'Están cumpliendo.',
  recien_pactado: 'Se pactaron hace poco: todavía no les ha dado tiempo.',
  completado:     'Ya devolvieron todo el capital. Puede cerrar estos préstamos.',
};

function Acuerdos() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    axios.get('/api/acuerdos')
      .then(r => setDatos(r.data || { prestamos: [], pagos: [] }))
      .catch(() => setDatos({ prestamos: [], pagos: [] }))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) return <div className="table-container"><p>Cargando…</p></div>;

  const r = acuerdosDeDevolucion(datos.prestamos, datos.pagos, new Date());

  if (r.cuantos === 0) {
    return (
      <div className="table-container">
        <h2 style={{ marginTop: 0 }}>🤝 Acuerdos de devolución</h2>
        <p style={{ color: '#6b7280' }}>
          Aquí salen los préstamos que dejó <strong>sin interés</strong> — esos
          en los que quedó con el cliente en que solo le devuelva el capital.
          Todavía no tiene ninguno.
        </p>
        <p style={{ color: '#9ca3af', fontSize: '13px' }}>
          Un préstamo entra aquí cuando le pone <strong>0%</strong> de interés.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="table-container" style={{ marginBottom: '16px' }}>
        <h2 style={{ marginTop: 0 }}>🤝 Acuerdos de devolución</h2>
        <p style={{ color: '#6b7280', marginTop: '-6px' }}>
          Préstamos que quedaron sin interés: solo falta que devuelvan el capital.
          No salen en «Por cobrar» ni en «Morosos», por eso están aquí.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '14px' }}>
          {[['Acuerdos', r.cuantos, '#374151'],
            ['Prestado', moneda(r.prestado), '#374151'],
            ['Ya devolvieron', moneda(r.devuelto), '#166534'],
            ['Falta por recuperar', moneda(r.falta), '#991b1b']].map(([t, v, c]) => (
            <div key={t} style={{ border: '1px solid #e5e7eb', borderRadius: '8px',
                                  padding: '10px 16px', minWidth: '130px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{t}</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: c }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {r.grupos.map(g => {
        const p = PINTA[g.clave] || PINTA.completado;
        const falta = g.acuerdos.reduce((s, a) => s + a.falta, 0);
        return (
          <div key={g.clave} className="table-container" style={{ marginBottom: '16px' }}>
            <div style={{ background: p.fondo, border: `1px solid ${p.borde}`,
                          borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
              <strong style={{ color: p.color, fontSize: '16px' }}>
                {p.icono} {g.titulo}
              </strong>
              <span style={{ color: p.color, marginLeft: '8px' }}>
                — {g.acuerdos.length}
                {falta > 0 && `, faltan ${moneda(falta)}`}
              </span>
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '3px' }}>
                {PIE[g.clave]}
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="payments-table" style={{ minWidth: '34rem' }}>
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Prestado</th>
                    <th style={{ textAlign: 'right' }}>Ha devuelto</th>
                    <th style={{ textAlign: 'right' }}>Falta</th>
                    <th>Última devolución</th>
                  </tr>
                </thead>
                <tbody>
                  {g.acuerdos.map(a => (
                    <tr key={a.prestamo.id}>
                      <td>
                        <strong>{a.prestamo.client_name || a.prestamo.name}</strong>
                        {a.prestamo.phone && (
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                            {a.prestamo.phone}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{moneda(a.prestado)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {a.devuelto > 0 ? moneda(a.devuelto) : '—'}
                        {/* El porcentaje al lado, porque «devolvió 7 millones»
                            no dice si va por la mitad o por la décima parte. */}
                        {a.devuelto > 0 && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {a.porcentaje}% · {a.devoluciones}{' '}
                            {a.devoluciones === 1 ? 'vez' : 'veces'}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                                   color: a.falta > 0 ? '#991b1b' : '#166534' }}>
                        {moneda(a.falta)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* En días, no en fecha: «hace 19 meses» pesa, «25 dic
                            2024» hay que restarlo de cabeza. La fecha va al
                            lado en pequeño, para quien quiera comprobarla. */}
                        {a.ultimaDevolucion ? (
                          <>
                            <span style={{ color: p.color, fontWeight: 600 }}>
                              {enPalabras(a.diasSinDevolver)}
                            </span>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              {formatearFecha(a.ultimaDevolucion)}
                            </div>
                          </>
                        ) : (
                          <>
                            <span style={{ color: p.color, fontWeight: 600 }}>Nunca</span>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              prestado {enPalabras(a.diasSinDevolver)}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// «hace 19 meses» se entiende de una; «hace 583 días» hay que dividirlo.
function enPalabras(dias) {
  if (dias === null || dias === undefined) return '—';
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'hace 1 día';
  if (dias < 45) return `hace ${dias} días`;
  const meses = Math.round(dias / 30.44);
  if (meses < 24) return `hace ${meses} meses`;
  return `hace ${Math.floor(meses / 12)} años`;
}

export default Acuerdos;
